import React, {useState, useMemo, useEffect} from 'react';
import {SMCSelect, SMCOption} from '../../../components/Simple/SMCSelect';
import {useTranslation} from 'react-i18next';
import {SourceDestinationTypeValues, ServiceTypeValues, SourceDestinationZoneValues, ServiceCustomTypeValues, ActionValues, LogValues, CpRuleSchema} from '../../../api/DBSchema/CPRule';
import SMCModalButtonContainer from '../../../components/core/SMCModal/SMCModalButtonContainer';
import SMCButton from '../../../components/Simple/SMCButton';
import SMCModalBody from '../../../components/core/SMCModal/SMCModalBody';
import SMCTextInput from '../../../components/Simple/SMCTextInput';
import EmptyRow from '../../../components/Simple/EmptyRow';
import SMCModal from '../../../components/core/SMCModal';
import {SMCCheckbox} from '../../../components/Simple/SMCCheckbox';
import Api from '../../../api/SMPFetchHelper';
import {useSMCSnackbar} from '../../../utils/hooks';
import {useSMCErrorHandler} from '../../../utils/errorUtils';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';

function AccessPolicyLegacyIncomingEditorModal({
    baseObjectName,
    isOpen = false,
    width = 710,
    height = 610,
    onCancel,
    onSuccess,
    defaultObject,
    objectType
}) {
    const { t } = useTranslation();
    const { enqueueErrorSnackbar } = useSMCSnackbar();
    const { getErrorMessage } = useSMCErrorHandler();

    const sourceOptions = useMemo(() => (
        <>
        <SMCOption value={SourceDestinationTypeValues.ANY}>{t('SMP.PLAN.SEC_SOFT_BLADES.ANY')}</SMCOption>
        <SMCOption value={SourceDestinationTypeValues.HOST}>{t('Single IP address')}</SMCOption>
        <SMCOption value={SourceDestinationTypeValues.IP_RANGE}>{t('IP address range')}</SMCOption>
        <SMCOption value={SourceDestinationTypeValues.NETWORK}>{t('SMP.NETWORK')}</SMCOption>
        <SMCOption value={SourceDestinationTypeValues.ZONE}>{t('SMP.PLAN.SEC_SOFT_BLADES.ZONE')}</SMCOption>
        </>
    ), [t]);

    const sourceZoneTypeOptions = useMemo(() => (
        <>
        <SMCOption value={SourceDestinationZoneValues.USERS}>{t('SMP.PLAN.SEC_SOFT_BLADES.ALL_IDENTIFIED_USERS')}</SMCOption>
        <SMCOption value={SourceDestinationZoneValues.DMZ}>{t('SMP.PLAN.SEC_SOFT_BLADES.DMZ_NETWORK')}</SMCOption>
        <SMCOption value={SourceDestinationZoneValues.INTERNET}>{t('SMP.PLAN.SEC_SOFT_BLADES.INTERNET')}</SMCOption>
        <SMCOption value={SourceDestinationZoneValues.LAN}>{t('SMP.PLAN.SEC_SOFT_BLADES.LAN_NETWORKS')}</SMCOption>
        <SMCOption value={SourceDestinationZoneValues.LAN_DMZ}>{t('SMP.PLAN.SEC_SOFT_BLADES.LAN_DMZ_NETWORKS')}</SMCOption>
        <SMCOption value={SourceDestinationZoneValues.GATEWAY}>{t('SMP.PLAN.SEC_SOFT_BLADES.THIS_GATEWAY')}</SMCOption>
        <SMCOption value={SourceDestinationZoneValues.TRUSTED_WLAN}>{t('SMP.PLAN.SEC_SOFT_BLADES.TRUSTED_WIRELESS_NETWORKS')}</SMCOption>
        <SMCOption value={SourceDestinationZoneValues.UNTRUSTED_WLAN}>{t('SMP.PLAN.SEC_SOFT_BLADES.UNTRUSTED_WIRELESS_NETWORKS')}</SMCOption>
        <SMCOption value={SourceDestinationZoneValues.REMOTE_ACCESS}>{t('SMP.PLAN.SEC_SOFT_BLADES.REMOTE_ACCESS')}</SMCOption>
        <SMCOption value={SourceDestinationZoneValues.WIRELESS}>{t('SMP.PLAN.SEC_SOFT_BLADES.WIRELESS_NETWORKS')}</SMCOption>
        </>
    ), [t]);

    const destinationOptions = useMemo(() => (
        <>
        <SMCOption value={SourceDestinationTypeValues.ANY}>{t('SMP.PLAN.SEC_SOFT_BLADES.ANY')}</SMCOption>
        <SMCOption value={SourceDestinationTypeValues.HOST}>{t('Single IP address')}</SMCOption>
        <SMCOption value={SourceDestinationTypeValues.IP_RANGE}>{t('IP address range')}</SMCOption>
        <SMCOption value={SourceDestinationTypeValues.NETWORK}>{t('SMP.NETWORK')}</SMCOption>
        <SMCOption value={SourceDestinationTypeValues.ZONE}>{t('SMP.PLAN.SEC_SOFT_BLADES.ZONE')}</SMCOption>
        </>
    ), [t]);

    const destinationZoneTypeOptions = useMemo(() => (
        <>
        <SMCOption value={SourceDestinationZoneValues.USERS}>{t('SMP.PLAN.SEC_SOFT_BLADES.ALL_IDENTIFIED_USERS')}</SMCOption>
        <SMCOption value={SourceDestinationZoneValues.DMZ}>{t('SMP.PLAN.SEC_SOFT_BLADES.DMZ_NETWORK')}</SMCOption>
        <SMCOption value={SourceDestinationZoneValues.LAN}>{t('SMP.PLAN.SEC_SOFT_BLADES.LAN_NETWORKS')}</SMCOption>
        <SMCOption value={SourceDestinationZoneValues.LAN_DMZ}>{t('SMP.PLAN.SEC_SOFT_BLADES.LAN_DMZ_NETWORKS')}</SMCOption>
        <SMCOption value={SourceDestinationZoneValues.GATEWAY}>{t('SMP.PLAN.SEC_SOFT_BLADES.THIS_GATEWAY')}</SMCOption>
        <SMCOption value={SourceDestinationZoneValues.TRUSTED_WLAN}>{t('SMP.PLAN.SEC_SOFT_BLADES.TRUSTED_WIRELESS_NETWORKS')}</SMCOption>
        <SMCOption value={SourceDestinationZoneValues.UNTRUSTED_WLAN}>{t('SMP.PLAN.SEC_SOFT_BLADES.UNTRUSTED_WIRELESS_NETWORKS')}</SMCOption>
        <SMCOption value={SourceDestinationZoneValues.REMOTE_ACCESS}>{t('SMP.PLAN.SEC_SOFT_BLADES.REMOTE_ACCESS')}</SMCOption>
        <SMCOption value={SourceDestinationZoneValues.WIRELESS}>{t('SMP.PLAN.SEC_SOFT_BLADES.WIRELESS_NETWORKS')}</SMCOption>
        </>
    ), [t]);

    const serviceTypeOptions = useMemo(() => (
        <>
        <SMCOption value={ServiceTypeValues.ANY}>{t('SMP.PLAN.SEC_SOFT_BLADES.ANY')}</SMCOption>
        <SMCOption value={ServiceTypeValues.CUSTOM}>{t('SMP.PLAN.SEC_SOFT_BLADES.CUSTOM')}</SMCOption>
        <SMCOption value={ServiceTypeValues.ANY_TCP}>{'Any TCP'}</SMCOption>
        <SMCOption value={ServiceTypeValues.ANY_UDP}>{'Any UDP'}</SMCOption>
        <SMCOption value={ServiceTypeValues.HTTP}>{'HTTP'}</SMCOption>
        <SMCOption value={ServiceTypeValues.HTTPS}>{'HTTPS'}</SMCOption>
        <SMCOption value={ServiceTypeValues.FTP}>{'FTP'}</SMCOption>
        <SMCOption value={ServiceTypeValues.SNMP}>{'SNMP'}</SMCOption>
        <SMCOption value={ServiceTypeValues.SSH}>{'SSH'}</SMCOption>
        <SMCOption value={ServiceTypeValues.TFTP}>{'TFTP'}</SMCOption>
        <SMCOption value={ServiceTypeValues.TELNET}>{'TELNET'}</SMCOption>
        <SMCOption value={ServiceTypeValues.POP3}>{'POP3'}</SMCOption>
        <SMCOption value={ServiceTypeValues.ICMP}>{'ICMP'}</SMCOption>
        <SMCOption value={ServiceTypeValues.IMAP}>{'IMAP'}</SMCOption>
        <SMCOption value={ServiceTypeValues.SMTP}>{'SMTP'}</SMCOption>
        <SMCOption value={ServiceTypeValues.DHCP}>{'DHCP'}</SMCOption>
        <SMCOption value={ServiceTypeValues.CIFS}>{'CIFS'}</SMCOption>
        <SMCOption value={ServiceTypeValues.DNS_TCP}>{'DNS_TCP'}</SMCOption>
        <SMCOption value={ServiceTypeValues.DNS_UDP}>{'DNS_UDP'}</SMCOption>
        <SMCOption value={ServiceTypeValues.LDAP}>{'LDAP'}</SMCOption>
        <SMCOption value={ServiceTypeValues.SIP_TCP}>{'SIP_TCP'}</SMCOption>
        <SMCOption value={ServiceTypeValues.SIP_UDP}>{'SIP_UDP'}</SMCOption>
        </>
    ), [t]);

    const serviceCustomTypeOptions = useMemo(() => (
        <>
        <SMCOption value={ServiceCustomTypeValues.TCP}>{'TCP'}</SMCOption>
        <SMCOption value={ServiceCustomTypeValues.UDP}>{'UDP'}</SMCOption>
        <SMCOption value={ServiceCustomTypeValues.ICMP}>{'ICMP'}</SMCOption>
        <SMCOption value={ServiceCustomTypeValues.OTHER}>{t('SMP.PLAN.SEC_SOFT_BLADES.OTHER')}</SMCOption>
        </>
    ), [t]);

    const [sourceType, setSourceType] = useState(SourceDestinationTypeValues.ANY);
    const [sourceSingleIp, setSourceSingleIp] = useState('');
    const [sourceStartIp, setSourceStartIp] = useState('');
    const [sourceEndIp, setSourceEndIp] = useState('');
    const [sourceNetworkIp, setSourceNetworkIp] = useState('');
    const [sourceNetworkSubnetMask, setSourceNetworkSubnetMask] = useState('');
    const [sourceZoneType, setSourceZoneType] = useState(SourceDestinationZoneValues.USERS);

    const [destinationType, setDestinationType] = useState(SourceDestinationTypeValues.ANY);
    const [destinationSingleIp, setDestinationSingleIp] = useState('');
    const [destinationStartIp, setDestinationStartIp] = useState('');
    const [destinationEndIp, setDestinationEndIp] = useState('');
    const [destinationNetworkIp, setDestinationNetworkIp] = useState('');
    const [destinationNetworkSubnetMask, setDestinationNetworkSubnetMask] = useState('');
    const [destinationZoneType, setDestinationZoneType] = useState(SourceDestinationZoneValues.USERS);
    
    const [serviceType, setServiceType] = useState(ServiceTypeValues.ANY);
    const [serviceCustomType, setServiceCustomType] = useState(ServiceCustomTypeValues.OTHER);
    const [servicePorts, setServicePorts] = useState('');
    const [serviceIcmpCode, setServiceIcmpCode] = useState('');
    const [serviceIcmpType, setServiceIcmpType] = useState('');
    const [serviceIpProtocol, setServiceIpProtocol] = useState('');
    
    const [action, setAction] = useState(ActionValues.BLOCK);
    const [log, setLog] = useState(LogValues.LOG);
    const [description, setDescription] = useState('');
    const [enabled, setEnabled] = useState(true);

    const sourceDestinationSchema = CpRuleSchema.sourceDestinationSchema;
    const serviceSchema           = CpRuleSchema.serviceSchema;

    const onFinish = async () => {
        const source = {
            [sourceDestinationSchema.type]: sourceType,
            [sourceDestinationSchema.zone]: sourceZoneType,
            [sourceDestinationSchema.ipSingle]: sourceSingleIp,
            [sourceDestinationSchema.ipStart]: sourceStartIp,
            [sourceDestinationSchema.ipEnd]: sourceEndIp,
            [sourceDestinationSchema.ipNetwork]: sourceNetworkIp,
            [sourceDestinationSchema.ipMask]: sourceNetworkSubnetMask,
        }

        const destination = {
            [sourceDestinationSchema.type]: destinationType,
            [sourceDestinationSchema.zone]: destinationZoneType,
            [sourceDestinationSchema.ipSingle]: destinationSingleIp,
            [sourceDestinationSchema.ipStart]: destinationStartIp,
            [sourceDestinationSchema.ipEnd]: destinationEndIp,
            [sourceDestinationSchema.ipNetwork]: destinationNetworkIp,
            [sourceDestinationSchema.ipMask]: destinationNetworkSubnetMask,
        }

        const service = {
            [serviceSchema.type]: serviceType,
            [serviceSchema.customType]: serviceCustomType,
            [serviceSchema.ports]: servicePorts,
            [serviceSchema.icmpType]: serviceIcmpType,
            [serviceSchema.icmpCode]: serviceIcmpCode,
            [serviceSchema.ipProtocol]: serviceIpProtocol
        }

        const retVal = {
            [CpRuleSchema.source]: source,
            [CpRuleSchema.destination]: destination,
            [CpRuleSchema.service]: service,
            [CpRuleSchema.action]: action,
            [CpRuleSchema.log]: log,
            [CpRuleSchema.description]: description,
            [CpRuleSchema.enabled]: enabled,
        };
        try {
            await Api.validateAccessPolicy(objectType, baseObjectName, retVal);
        } catch (e) {
            const errorMessage = getErrorMessage(e);
            enqueueErrorSnackbar(errorMessage, {
                anchorOrigin: {
                    horizontal: 'center',
                    vertical: 'bottom'
                }
            });
            return;
        }
        onSuccess(retVal);
    }

    // On each new open, reset values
    useEffect(() => {
        if (isOpen) {
            setSourceType(SourceDestinationTypeValues.ANY);
            setSourceSingleIp('');
            setSourceStartIp('');
            setSourceEndIp('');
            setSourceNetworkIp('');
            setSourceNetworkSubnetMask('');
            setSourceZoneType(SourceDestinationZoneValues.USERS);

            setDestinationType(SourceDestinationTypeValues.ANY);
            setDestinationSingleIp('');
            setDestinationStartIp('');
            setDestinationEndIp('');
            setDestinationNetworkIp('');
            setDestinationNetworkSubnetMask('');
	    setDestinationZoneType(SourceDestinationZoneValues.USERS);

            setServiceType(ServiceTypeValues.ANY);
            setServiceCustomType(ServiceCustomTypeValues.TCP);
            setServicePorts('');
            setServiceIcmpCode('0');
            setServiceIcmpType('0');
            setServiceIpProtocol('1');
            setAction(ActionValues.BLOCK);
            setLog(LogValues.LOG);
            setDescription('');
            setEnabled(true);
        }

        if (defaultObject) {
            if (defaultObject[CpRuleSchema.source]) {
                const source = defaultObject[CpRuleSchema.source];
                setSourceType(source[sourceDestinationSchema.type]);
                setSourceZoneType(source[sourceDestinationSchema.zone]);
                setSourceSingleIp(source[sourceDestinationSchema.ipSingle]);
                setSourceStartIp(source[sourceDestinationSchema.ipStart]);
                setSourceEndIp(source[sourceDestinationSchema.ipEnd]);
                setSourceNetworkIp(source[sourceDestinationSchema.ipNetwork]);
                setSourceNetworkSubnetMask(source[sourceDestinationSchema.ipMask]);
            }
            if (defaultObject[CpRuleSchema.destination]) {
                const destination = defaultObject[CpRuleSchema.destination];
                setDestinationType(destination[sourceDestinationSchema.type]);
		setDestinationZoneType(destination[sourceDestinationSchema.zone]);
                setDestinationSingleIp(destination[sourceDestinationSchema.ipSingle]);
                setDestinationStartIp(destination[sourceDestinationSchema.ipStart]);
                setDestinationEndIp(destination[sourceDestinationSchema.ipEnd]);
                setDestinationNetworkIp(destination[sourceDestinationSchema.ipNetwork]);
                setDestinationNetworkSubnetMask(destination[sourceDestinationSchema.ipMask]);
            }
            if (defaultObject[CpRuleSchema.service]) {
                const service = defaultObject[CpRuleSchema.service];
                setServiceType(service[serviceSchema.type]);
                setServiceCustomType(service[serviceSchema.customType]);
                setServicePorts(service[serviceSchema.ports]);
                setServiceIcmpType(service[serviceSchema.icmpType]);
                setServiceIcmpCode(service[serviceSchema.icmpCode]);
                setServiceIpProtocol(service[serviceSchema.ipProtocol]);
            }
            if (defaultObject[CpRuleSchema.action]) {
                setAction(defaultObject[CpRuleSchema.action]);
            }
            if (defaultObject[CpRuleSchema.description]) {
                setDescription(defaultObject[CpRuleSchema.description]);
            }
            if (defaultObject[CpRuleSchema.log]) {
                setLog(defaultObject[CpRuleSchema.log]);
            }
            if (defaultObject[CpRuleSchema.enabled]) {
                setEnabled(defaultObject[CpRuleSchema.enabled]);
            }
            else {
                setEnabled(false);
            }
        }
    }, [isOpen, defaultObject, sourceDestinationSchema, serviceSchema]);


    return (
        <SMCModal
            isOpen={isOpen}
            onRequestClose={onCancel}
            width={width}
            height={height}
            title={t('SMP.PLAN.SEC_SOFT_BLADES.FW_WIZ_INCOMING')}
        >
            <SMCModalBody>
                {/* Source */}
                <SMCSelect
                    value={sourceType}
                    onChange={e => setSourceType(e.target.value)}
                    width={220}
                    label={t('SMP.PLAN.SEC_SOFT_BLADES.SOURCE')}
                >
                    {sourceOptions}
                </SMCSelect>
                {
                    (() => {
                        switch (sourceType) {
                            case SourceDestinationTypeValues.HOST:
                                return (
                                    <SMCTextInput
                                        width={220}
                                        value={sourceSingleIp}
                                        onChange={e => setSourceSingleIp(e.target.value)}
                                        label="IPv4 address"
                                    />
                                );
                            case SourceDestinationTypeValues.IP_RANGE:
                                return (
                                    <>
                                        <SMCTextInput
                                            width={220}
                                            value={sourceStartIp}
                                            onChange={e => setSourceStartIp(e.target.value)}
                                            label={"Start IPv4"}
                                        />
                                        <SMCTextInput
                                            width={220}
                                            value={sourceEndIp}
                                            onChange={e => setSourceEndIp(e.target.value)}
                                            label={"End IPv4"}
                                        />
                                    </>
                                );
                            case SourceDestinationTypeValues.NETWORK:
                                return (
                                    <>
                                        <SMCTextInput
                                            width={220}
                                            value={sourceNetworkIp}
                                            onChange={e => setSourceNetworkIp(e.target.value)}
                                            label="Network IPv4 address"
                                        />
                                        <SMCTextInput
                                            width={220}
                                            value={sourceNetworkSubnetMask}
                                            onChange={e => setSourceNetworkSubnetMask(e.target.value)}
                                            label="Subnet mask"
                                        />
                                    </>
                                );
                            case SourceDestinationTypeValues.ZONE:
                                return (
                                    <SMCSelect
                                        width={220}
                                        value={sourceZoneType}
                                        onChange={e => setSourceZoneType(e.target.value)}
                                        label={t('SMP.PLAN.SEC_SOFT_BLADES.ZONE.TYPE')}
                                    >
                                        {sourceZoneTypeOptions}
                                    </SMCSelect>
                                );
                            default:
                                return (
                                    <EmptyRow />
                                )
                        }
                    })()
                }
                <EmptyRow />
                
                {/* Destination */}
                <SMCSelect
                    value={destinationType}
                    onChange={e => setDestinationType(e.target.value)}
                    width={220}
                    label={t('SMP.PLAN.SEC_SOFT_BLADES.DESTINATION')}
                >
                    {destinationOptions}
                </SMCSelect>
                {
                    (() => {
                        switch(destinationType) {
                            case SourceDestinationTypeValues.HOST:
                                return (
                                    <SMCTextInput
                                        width={220}
                                        value={destinationSingleIp}
                                        onChange={e => setDestinationSingleIp(e.target.value)}
                                        label="IPv4 address"
                                    />
                                );
                            case SourceDestinationTypeValues.IP_RANGE:
                                return (
                                    <>
                                        <SMCTextInput
                                            width={220}
                                            value={destinationStartIp}
                                            onChange={e => setDestinationStartIp(e.target.value)}
                                            label="Start IPv4"
                                        />
                                        <SMCTextInput
                                            width={220}
                                            value={destinationEndIp}
                                            onChange={e => setDestinationEndIp(e.target.value)}
                                            label="End IPv4"
                                        />
                                    </>
                                );
                            case SourceDestinationTypeValues.NETWORK:
                                return (
                                    <>
                                        <SMCTextInput
                                            width={220}
                                            value={destinationNetworkIp}
                                            onChange={e => setDestinationNetworkIp(e.target.value)}
                                            label="Network IPv4 address"
                                        />
                                        <SMCTextInput
                                            width={220}
                                            value={destinationNetworkSubnetMask}
                                            onChange={e => setDestinationNetworkSubnetMask(e.target.value)}
                                            label="Subnet mask"
                                        />
                                    </>
                                );
                            case SourceDestinationTypeValues.ZONE:
                                return (
                                    <SMCSelect
                                        width={220}
                                        value={destinationZoneType}
                                        onChange={e => setDestinationZoneType(e.target.value)}
                                        label={t('SMP.PLAN.SEC_SOFT_BLADES.ZONE.TYPE')}
                                    >
                                        {destinationZoneTypeOptions}
                                    </SMCSelect>
                                );    
                            default:
                                return (
                                    <EmptyRow />
                                );
                        }
                    })()
                }
                <EmptyRow />

                {/* Service */}
                <SMCSelect
                    value={serviceType}
                    onChange={e => setServiceType(e.target.value)}
                    width={220}
                    label={t('SMP.PLAN.SEC_SOFT_BLADES.SERVICE')}
                >
                    {serviceTypeOptions}
                </SMCSelect>
                {
                    serviceType === ServiceTypeValues.CUSTOM && (
                        <SMCSelect
                            value={serviceCustomType}
                            onChange={e => setServiceCustomType(e.target.value)}
                            width={220}
                            label={t('SMP.TYPE')}
                        >
                            {serviceCustomTypeOptions}
                        </SMCSelect>
                    )
                }
                {
                    serviceType === ServiceTypeValues.CUSTOM && (
                        (() => {
                            switch(serviceCustomType) {
                                case ServiceCustomTypeValues.TCP:
                                case ServiceCustomTypeValues.UDP:
                                    return (
                                            <SMCTextInput
                                                width={220}
                                                value={servicePorts}
                                                onChange={e => setServicePorts(e.target.value)}
                                                label={"Ports"}
                                                tooltip={t('com.sofaware.db.INVALID_PORTS_FORMAT')}
                                            />
                                    );
                                case ServiceCustomTypeValues.ICMP:
                                    return (
                                        <>
                                            <SMCTextInput
                                                width={220}
                                                value={serviceIcmpType}
                                                onChange={e => setServiceIcmpType(e.target.value)}
                                                label={"ICMP type"}
                                            />
                                            <SMCTextInput
                                                width={220}
                                                value={serviceIcmpCode}
                                                onChange={e => setServiceIcmpCode(e.target.value)}
                                                label={"ICMP code"}
                                            />
                                        </>
                                    );
                                case ServiceCustomTypeValues.OTHER:
                                    return (
                                        <>
                                            <SMCTextInput
                                                width={220}
                                                value={serviceIpProtocol}
                                                onChange={e => setServiceIpProtocol(e.target.value)}
                                                label={"IP Protocol"}
                                            />
                                        </>
                                    );
                                default:
                                    return (
                                        <EmptyRow />
                                    )
                            }
                        })()
                    )
                }
                
                <EmptyRow />

                <SMCSelect
                    width={220}
                    value={action}
                    onChange={e => setAction(e.target.value)}
                    label={t("SMP.ACTION")}
                >
                    <SMCOption value={ActionValues.ACCEPT}>{t('SMP.PLAN.SEC_SOFT_BLADES.ACCEPT')}</SMCOption>
                    <SMCOption value={ActionValues.BLOCK}>{t('SMP.PLAN.SEC_SOFT_BLADES.BLOCK')}</SMCOption>
                </SMCSelect>

                <SMCSelect
                    width={220}
                    value={log}
                    onChange={e => setLog(e.target.value)}
                    label={t("SMP.LOG")}
                >
                    <SMCOption value={LogValues.NONE}>{t('SMP.NONE')}</SMCOption>
                    <SMCOption value={LogValues.LOG}>{t('SMP.LOG')}</SMCOption>
                    <SMCOption value={LogValues.ALERT}>{t('SMP.ALERT')}</SMCOption>
                    <SMCOption value={LogValues.ACCOUNT}>{t('SMP.PLAN.SEC_SOFT_BLADES.ACCOUNT')}</SMCOption>
                </SMCSelect>

                <SMCTextInput
                    width={220}
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    label={t('SMP.DESCRIPTION')}
                />

                <SMCCheckbox
                    checked={enabled}
                    onChange={e => setEnabled(e.target.checked)}
                    label={t('SMP.ENABLED')}
                />

            </SMCModalBody>
            <SMCModalButtonContainer>
                <SMCButton onClick={onFinish}>
                    <FontAwesomeIcon style={{ marginTop: '3px', marginRight: '5px' }} color="green" icon="check" />
                    {t('SMC_GEN_FINISH')}
                </SMCButton>
                <SMCButton onClick={onCancel}>
                    <FontAwesomeIcon style={{ marginTop: '3px', marginRight: '5px' }} color="grey" icon="times" />
                    {t('SMC_GEN_CANCEL')}
                </SMCButton>
            </SMCModalButtonContainer>
        </SMCModal>
    );
}

export default AccessPolicyLegacyIncomingEditorModal;